import { useCallback } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router'

import { AskAboutThis } from '../../components/assistant/AskAboutThis'
import { usePublishAssistantPage } from '../../contexts/AssistantContext'
import { ASSISTANT_ENABLED } from '../../lib/features'
import { experimentDate } from '../../lib/format'
import { ExperimentResultContent } from '../../components/experiments/ExperimentResultContent'
import { comparisonScoreLabels, type ComparisonScoreLabels } from '../../components/comparison/ComparisonLabels'
import { PageHeader } from '../../components/PageHeader'
import { ProblemNotice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { getDailyStatus, getRecording } from '../../lib/api'
import { getPlan, getResult, getRun, listActivities } from '../../lib/experiments/api'
import { ApiError } from '../../lib/problem'
import { useResource } from '../../lib/useResource'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { NotFound } from '../NotFound'
import '../../styles/experiments-compare.css'
import '../../styles/experiment-result.css'

export function ExperimentResult() {
  const { id } = useParams<{ id: string }>()
  const [query] = useSearchParams()
  const { account } = useAuth()
  const revisionText = query.get('revision')
  const revision = revisionText === null ? undefined : Number(revisionText)
  if (account?.experiments_enabled_for_me !== true || id === undefined || (revision !== undefined && (!Number.isSafeInteger(revision) || revision < 1))) return <NotFound />
  return <ResultBody key={`${account?.user_id ?? 'none'}/${id}/${revision ?? 'latest'}`} id={id} revision={revision} />
}

function ResultBody({ id, revision }: { id: string; revision: number | undefined }) {
  const { account } = useAuth()
  const userId = account?.user_id
  const assistantEnabled = ASSISTANT_ENABLED && account != null && 'experiments_enabled_for_me' in account && account.experiments_enabled_for_me === true
  const load = useCallback(async (signal: AbortSignal) => {
    try {
      // Read the guarded endpoint first: no recording result or daily status is fetched before it permits exposure.
      const result = await getResult(id, { revision, signal })
      if (result.schema_version !== '1') throw new Error('The experiment result version is not supported.')
      if (revision !== undefined && result.revision !== revision) throw new Error('The result did not match the requested revision.')
      const run = await getRun(id, signal)
      if (run.id !== id || run.user_id !== userId) throw new Error('The run did not match this account.')
      if (run.report_state === 'pending') return { kind: 'pending' as const }
      if (run.before_recording_id !== result.comparison.before_recording_id || run.after_recording_id !== result.comparison.after_recording_id) throw new Error('The result did not match the run’s readings.')
      const [plan, before, after] = await Promise.all([
        getPlan(run.plan_id, signal), getRecording(result.comparison.before_recording_id, { signal }), getRecording(result.comparison.after_recording_id, { signal }),
      ])
      if (plan.id !== run.plan_id || plan.user_id !== userId || before.recording_id !== run.before_recording_id || after.recording_id !== run.after_recording_id || before.user_id !== userId || after.user_id !== userId) throw new Error('The experiment sources did not match the run.')
      let activityName = plan.custom_name ?? run.activity_key
      let activityLimit: string | null = null
      try {
        const library = await listActivities(signal)
        if (library.library_version === plan.library_version) {
          const activity = library.activities.find((item) => item.key === run.activity_key && item.library_version === plan.library_version)
          activityName = plan.custom_name ?? activity?.name ?? activityName
          activityLimit = activity?.limit_text ?? null
        }
      } catch { signal.throwIfAborted() }
      if (run.activity_key === 'coffee' && !activityLimit) throw new Error('The coffee limits for this saved experiment are unavailable. Recheck the saved activity context.')
      let scoreLabels: ComparisonScoreLabels = {}
      if (result.comparison.category === 'comparable' && Object.keys(result.comparison.score_deltas ?? {}).length !== 0) {
        try { scoreLabels = comparisonScoreLabels(await getDailyStatus(before.recording_id, { signal }), before.recording_id) } catch { signal.throwIfAborted() }
      }
      signal.throwIfAborted()
      return { kind: 'ready' as const, result, run, plan, before, after, activityName, activityLimit, scoreLabels }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'experiment.report_pending') return { kind: 'pending' as const }
      throw error
    }
  }, [id, revision, userId])
  const read = useResource(load)
  const [landing, armLanding] = useRetryLanding(read.error !== null)
  usePublishAssistantPage(assistantEnabled && read.data?.kind === 'ready' ? {
    dateLabel: experimentDate(read.data.before.local_date ?? read.data.before.session_date),
    activityLabel: read.data.plan.custom_name != null || read.data.activityName !== read.data.run.activity_key ? read.data.activityName : null,
  } : null)
  if (!read.isLoading && read.error === null && read.data?.kind === 'pending') return <Navigate replace to={`/record/experiment/${encodeURIComponent(id)}/report`} />
  if (read.isLoading || read.error !== null || read.data?.kind !== 'ready') return <div className="stack stack--wide experiment-result">
    <PageHeader title="Experiment result" eyebrow="Experiments" back={<Link className="page__back" to="/experiments">← Your playbook</Link>} />
    {read.error !== null ? <ProblemNotice error={read.error} onRetry={read.reload} retryBusy={read.isLoading} landing={armLanding}>This result could not be read.</ProblemNotice> : <p role="status">Loading the experiment result…</p>}
  </div>
  return <ExperimentResultContent {...read.data} headingRef={landing} assistantAffordance={assistantEnabled && <AskAboutThis comparison />} />
}
